import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export function useDirectRealtime(conversationId: string | undefined, selfName: string | null) {
  const qc = useQueryClient();
  const [typing, setTyping] = useState<string[]>([]);
  const typingRef = useRef<Map<string, number>>(new Map());
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase.channel(`dm:${conversationId}`, {
      config: { broadcast: { self: false } },
    });

    channel
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
        () => {
          void qc.invalidateQueries({ queryKey: ['dm', 'messages', conversationId] });
        },
      )
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_attachments' }, () => {
        void qc.invalidateQueries({ queryKey: ['dm', 'messages', conversationId] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_reactions' }, () => {
        void qc.invalidateQueries({ queryKey: ['dm', 'reactions', conversationId] });
      })
      .on('broadcast', { event: 'typing' }, (msg) => {
        const name = (msg.payload as { name?: string })?.name;
        if (!name) return;
        typingRef.current.set(name, Date.now());
        setTyping([...typingRef.current.keys()]);
      })
      .subscribe();

    channelRef.current = channel;

    const interval = setInterval(() => {
      const now = Date.now();
      let changed = false;
      for (const [name, ts] of typingRef.current) {
        if (now - ts > 3500) {
          typingRef.current.delete(name);
          changed = true;
        }
      }
      if (changed) setTyping([...typingRef.current.keys()]);
    }, 1500);

    return () => {
      clearInterval(interval);
      void supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [conversationId, qc]);

  const lastSent = useRef(0);
  function sendTyping() {
    if (!selfName || !channelRef.current) return;
    const now = Date.now();
    if (now - lastSent.current < 1500) return;
    lastSent.current = now;
    void channelRef.current.send({ type: 'broadcast', event: 'typing', payload: { name: selfName } });
  }

  return { typing, sendTyping };
}
